import { useState } from "react";
import { api, formatApiErrorDetail } from "../lib/apiClient";
import { useAuth } from "../context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { toast } from "sonner";
import { Loader2, KeyRound } from "lucide-react";

export default function ChangePasswordDialog() {
  const { user, setUser, logout } = useAuth();
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [saving, setSaving] = useState(false);
  const open = !!user && user.must_change_password;
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const mismatch = form.confirm && form.confirm !== form.new_password;

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/auth/change-password", { current_password: form.current_password, new_password: form.new_password });
      setUser(data.user);
      toast.success("Password updated — you're all set.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open}>
      <DialogContent data-testid="change-password-dialog" className="sm:max-w-md [&>button]:hidden" onPointerDownOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><KeyRound className="w-5 h-5 text-[#4A6B53]" strokeWidth={1.5} /> Choose your password</DialogTitle>
          <DialogDescription>Welcome, {user?.name?.split(" ")[0]}! You signed in with a temporary password. Pick your own to continue (at least 8 characters).</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label className="text-[10px] font-semibold text-[#7A8C82] uppercase tracking-wider">Temporary password</Label>
            <Input data-testid="current-password-input" type="password" value={form.current_password} onChange={set("current_password")} className="h-12 bg-[#F9F9F6] border-[#E2DFD8]" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-[10px] font-semibold text-[#7A8C82] uppercase tracking-wider">New password</Label>
            <Input data-testid="new-password-input" type="password" value={form.new_password} onChange={set("new_password")} className="h-12 bg-[#F9F9F6] border-[#E2DFD8]" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-[10px] font-semibold text-[#7A8C82] uppercase tracking-wider">Type it again</Label>
            <Input data-testid="confirm-password-input" type="password" value={form.confirm} onChange={set("confirm")} className={`h-12 bg-[#F9F9F6] ${mismatch ? "border-[#C26D5C]" : "border-[#E2DFD8]"}`} />
            {mismatch && <p data-testid="password-mismatch" className="text-xs text-[#C26D5C]">Those don't match yet.</p>}
          </div>
        </div>
        <DialogFooter className="sm:justify-between gap-2">
          <Button data-testid="change-password-logout-btn" variant="ghost" onClick={logout} className="rounded-full text-[#7A8C82]">Log out</Button>
          <Button data-testid="change-password-submit-btn" onClick={save} disabled={saving || !form.current_password || form.new_password.length < 8 || form.new_password !== form.confirm}
            className="rounded-full bg-[#4A6B53] hover:bg-[#3D5A45] text-white h-11 px-6">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Save password
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
